import {
  Card,
  Body1,
  Body2,
  Button,
  tokens,
} from '@fluentui/react-components';

interface EmptyStateCardProps {
  /** Icon element rendered at large size */
  icon: React.ReactNode;
  /** Primary headline */
  title: string;
  /** Activation-oriented description text */
  description: string;
  /** Optional CTA button label */
  actionLabel?: string;
  /** Optional CTA click handler */
  onAction?: () => void;
  /** Optional accent color for the icon */
  accentColor?: string;
}

/**
 * A shared empty-state card used across pages when there is no data to display.
 *
 * Replaces passive "No X found" messages with activation-oriented copy and CTAs.
 * Uses neutral styling — no red/error intent.
 */
export function EmptyStateCard({
  icon,
  title,
  description,
  actionLabel,
  onAction,
  accentColor,
}: EmptyStateCardProps) {
  return (
    <Card>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: `${tokens.spacingVerticalXXXL} ${tokens.spacingHorizontalXXL}`,
          gap: tokens.spacingVerticalM,
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: '3rem', color: accentColor || tokens.colorNeutralForeground3 }}>
          {icon}
        </div>
        <Body1 style={{ fontWeight: 600, color: tokens.colorNeutralForeground1 }}>
          {title}
        </Body1>
        <Body2 style={{ color: tokens.colorNeutralForeground3, maxWidth: '420px' }}>
          {description}
        </Body2>
        {actionLabel && onAction && (
          <Button appearance="primary" onClick={onAction} style={{ marginTop: tokens.spacingVerticalS }}>
            {actionLabel}
          </Button>
        )}
      </div>
    </Card>
  );
}
